"use client";

import React from "react";
import { Building2, Users, Award, ShieldAlert, Mic, ArrowRight, CircleCheck } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  title: "מרכז רפואי שמיר (אסף הרופא)",
  description: "ליווי סיעודי צמוד בבית החולים המרכזי באזור, המעניק מענה רפואי מקיף לתושבי ראשון לציון והסביבה.",
  icon: Building2,
  features: ["ליווי באשפוז יום", "השגחה לאחר ניתוח", "סיוע בבדיקות מורכבות"]
}, {
  title: "מרכז רפואי רבין (בילינסון)",
  description: "שירותי מטפלים סיעודיים זמינים להגעה מהירה למרכז הרפואי רבין עבור מטופלים הזקוקים לעזרה אישית.",
  icon: Users,
  features: ["תמיכה במחלקות אשפוז", "ליווי לבדיקות חוץ", "סיוע סיעודי פרטי"]
}, {
  title: "מרפאות קהילתיות בראשון",
  description: "פריסה רחבה של מטפלים בקרבת מרפאות קופות החולים בעיר: כללית, מכבי, מאוחדת ולאומית.",
  icon: Award,
  features: ["ליווי לתורים רפואיים", "סיוע ברכישת תרופות", "עזרה בביקורי בית"]
}, {
  title: "מענה חירום עירוני",
  description: "זמינות מיידית של צוותי טיפול בראשון לציון למקרים דחופים הדורשים התערבות סיעודית מהירה.",
  icon: ShieldAlert,
  features: ["הגעה תוך 5 שעות", "תיאום מול גורמי רווחה", "ליווי במצבי משבר"]
}];
export default function ConsultingServices() {
  const headerReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const gridReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const featuredReveal = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="258494" id="מיקומים-וביתנות-בראשון-לציון" className="w-full py-24 bg-background text-foreground overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        
        {}
        <div ref={headerReveal.ref} className={`flex flex-col items-center text-center max-w-3xl mx-auto mb-16 transition-all duration-1000 ease-out ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Badge variant="secondary" className="mb-4 bg-accent text-accent-foreground hover:bg-accent/20 border-none">
            זמינות מקומית בראשון לציון
          </Badge>
          <h2 className="text-4xl md:text-5xl font-default font-bold text-primary mb-6 tracking-tight">
            שירותי סיעוד בקרבת המרכזים הרפואיים
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-default">
            אנו מספקים מענה סיעודי מהיר ומקצועי בראשון לציון, כולל ליווי צמוד במרכז הרפואי שמיר (אסף הרופא), מרכז רפואי רבין ובמרפאות הקהילתיות בעיר.
          </p>
        </div>

        {}
        <div ref={gridReveal.ref} className={`grid grid-cols-1 md:grid-cols-2 gap-8 mb-24 transition-all duration-1000 delay-200 ease-out ${gridReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {services.map((service, index) => <Card key={index} data-index={index} className="group border-border/50 shadow-md hover:shadow-lg transition-all duration-300 bg-card overflow-hidden rounded-lg">
              <CardHeader className="pb-4">
                <div className="size-12 rounded-lg bg-accent flex items-center justify-center mb-4 group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                  <service.icon className="size-6 text-accent-foreground group-hover:text-primary-foreground transition-colors duration-300" />
                </div>
                <CardTitle className="text-2xl font-default text-secondary">{service.title}</CardTitle>
                <CardDescription className="text-base font-default mt-2">
                  {service.description}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2.5">
                  {service.features.map((feature, fIndex) => <li key={fIndex} data-index={fIndex} className="flex items-center text-sm font-default text-muted-foreground">
                      <CircleCheck className="size-4 ml-3 text-secondary shrink-0" />
                      {feature}
                    </li>)}
                </ul>
              </CardContent>
              <CardFooter className="pt-2 pb-6">
                <Button variant="ghost" className="p-0 h-auto text-primary hover:text-primary/80 hover:bg-transparent font-semibold group/btn" asChild>
                  <Link to="/NursingCaregiverInRishonLeZionWithinFiveHoursPage">
                    למידע נוסף <ArrowRight className="mr-2 size-4 transition-transform group-hover/btn:-translate-x-1 rotate-180" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>)}
        </div>

        {}
        <div ref={featuredReveal.ref} className={`relative rounded-lg overflow-hidden bg-secondary text-secondary-foreground shadow-xl transition-all duration-1000 delay-400 ease-out ${featuredReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <div className="grid grid-cols-1 lg:grid-cols-2 items-center">
            <div className="p-8 md:p-12 lg:p-16 flex flex-col justify-center h-full">
              <Badge className="w-fit mb-6 bg-accent text-accent-foreground hover:bg-accent/90 border-none">
                <Mic className="size-3 ml-1" /> ייעוץ והכוונה למשפחות
              </Badge>
              <h3 className="text-3xl md:text-4xl font-default font-bold mb-6 leading-tight">
                זקוקים למטפל סיעודי בראשון לציון עכשיו?
              </h3>
              <p className="text-secondary-foreground/80 font-default text-lg mb-8">
                הצוות המקצועי שלנו ערוך לספק פתרונות מיידיים בתוך 5 שעות בלבד. אנו מכירים היטב את המוסדות הרפואיים בראשון לציון ויודעים להעניק את המענה המדויק ביותר ליקירכם, בבית או בבית החולים.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold rounded-md" asChild>
                  <Link to="/ContactPage">
                    צרו קשר מיידי
                  </Link>
                </Button>
                <Button size="lg" variant="outline" className="border-secondary-foreground/20 text-secondary-foreground hover:bg-secondary-foreground/10 font-semibold rounded-md" asChild>
                  <Link to="/ProfessionalBlogPage">
                    מדריכי זכויות
                  </Link>
                </Button>
              </div>
            </div>
            <div className="relative h-full min-h-[300px] lg:min-h-full">
              <AspectRatio ratio={4 / 3} className="h-full">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1626204/img/rishon_medical_care.png" alt="שירותי סיעוד בראשון לציון" className="object-cover w-full h-full" loading="lazy" />
                {}
                <div className="absolute inset-0 bg-gradient-to-t from-secondary/80 to-transparent lg:bg-gradient-to-l lg:from-secondary lg:to-transparent pointer-events-none" />
              </AspectRatio>
            </div>
          </div>
        </div>

      </div>
    </section>;
}